/**
 * AdminSEOCitiesCard — admin manages the list of cities that show up in the
 * dynamic sitemap.xml. Each entry creates URLs like
 * `/best-wedding-photographer-<city>` for every event + vendor combination.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, FloppyDisk, Spinner, Plus, Trash, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSEOCitiesCard = () => {
  const [cities, setCities] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [newCity, setNewCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/seo-cities`, { withCredentials: true });
      setCities(data.cities || []);
      setDefaults(data.defaults || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load SEO cities');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addCity = () => {
    const v = (newCity || '').trim();
    if (!v) return;
    if (cities.some((c) => c.toLowerCase() === v.toLowerCase())) {
      toast.warning('Already in the list');
      return;
    }
    setCities((cur) => [...cur, v]);
    setNewCity('');
  };

  const removeCity = (i) => setCities((cur) => cur.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/seo-cities`,
        { cities }, { withCredentials: true });
      setCities(data.cities || []);
      toast.success(`Saved — ${(data.cities || []).length} cities in sitemap`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const resetToDefaults = () => {
    if (!window.confirm('Replace the current list with the default cities?')) return;
    setCities([...defaults]);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-seo-cities-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <MapPin size={20} className="text-brand-primary" weight="fill" /> SEO Cities
        </CardTitle>
        <CardDescription>
          Drives the dynamic <code className="font-mono">/api/sitemap.xml</code> — each city here
          generates URLs like <span className="font-mono">/best-wedding-photographer-{'<city>'}</span>{' '}
          for every event × vendor combination. Keep this list curated (≤100) so the sitemap stays
          focused on cities you actively serve.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary py-4 text-center">Loading…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
              {cities.length === 0 && (
                <p className="text-xs text-text-secondary italic col-span-full">
                  No cities yet — the sitemap will fall back to the default list.
                </p>
              )}
              {cities.map((c, i) => (
                <div key={i}
                  className="flex items-center gap-2 px-2 py-1.5 border border-[#E8E1D5] bg-sand-50"
                  data-testid={`admin-seo-city-${i}`}>
                  <span className="text-sm flex-1 truncate">{c}</span>
                  <button onClick={() => removeCity(i)}
                    className="text-brand-primary hover:text-[#7B2E22]"
                    data-testid={`admin-seo-city-remove-${i}`}>
                    <Trash size={13} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <Input value={newCity} onChange={(e) => setNewCity(e.target.value)}
                placeholder="e.g. Coimbatore"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCity(); } }}
                className="h-9 rounded-none border-[#E8E1D5] flex-1"
                data-testid="admin-seo-city-input" />
              <Button onClick={addCity} variant="outline"
                className="rounded-none border-[#E8E1D5] h-9"
                data-testid="admin-seo-city-add">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#E8E1D5]">
              <button onClick={resetToDefaults}
                className="text-xs text-text-secondary hover:text-brand-primary flex items-center gap-1"
                data-testid="admin-seo-cities-reset">
                <ArrowsClockwise size={12} /> Reset to defaults
              </button>
              <Button onClick={save} disabled={saving}
                className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
                data-testid="admin-seo-cities-save">
                {saving ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
                Save cities
              </Button>
            </div>

            <div className="text-[11px] text-text-secondary pt-1">
              <strong>{cities.length}</strong> cities × ~80 (event × vendor) combos ={' '}
              <strong>~{cities.length * 80}</strong> SEO URLs in the sitemap.{' '}
              <a href="/api/sitemap.xml" target="_blank" rel="noreferrer"
                className="text-brand-primary hover:underline">View sitemap.xml →</a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSEOCitiesCard;
